import type { AbilityLevel } from "@/lib/planner/skill-fit";
import { scoreOutOf100Label } from "@/lib/conditions/score-display";
import type { SessionRecord } from "./store";

export type ReportCardStat = {
  value: string;
  label: string;
};

export type ReportCardConditionsInput = {
  placeName?: string | null;
  score?: number | null;
  scoreLabel?: string | null;
  tempC?: number | null;
  windKmh?: number | null;
  windDir?: string | null;
  sky?: string | null;
  abilityLevel?: AbilityLevel | null;
};

export type ReportCardConditionsStamp = {
  primary: string;
  secondary: string | null;
};

const ABILITY_LABEL: Record<AbilityLevel, string> = {
  beginner: "Beginner level",
  intermediate: "Intermediate level",
  advanced: "Advanced level",
  expert: "Expert level",
};

const ABILITY_VALUES = new Set<AbilityLevel>(["beginner", "intermediate", "advanced", "expert"]);

export function skillStampLabel(level: unknown): string | null {
  if (typeof level !== "string" || !ABILITY_VALUES.has(level as AbilityLevel)) return null;
  return ABILITY_LABEL[level as AbilityLevel];
}

export function formatReportDuration(seconds: number): string {
  const totalMinutes = Math.max(1, Math.round(seconds / 60));
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

export function buildReportCardStats(record: SessionRecord): ReportCardStat[] {
  const s = record.stats;
  const totalSeconds = s.elapsedS > 0 ? s.elapsedS : Math.max(0, (record.endedAt - record.startedAt) / 1000);
  const avg = { value: `${Math.round(s.avgMovingSpeedKmh)}`, label: "AVG KM/H" };
  const total = { value: formatReportDuration(totalSeconds), label: "TOTAL" };

  if (record.sport === "surf") {
    return [
      { value: `${s.surf?.waveCount ?? 0}`, label: "WAVES" },
      { value: `${Math.round(s.surf?.longestRideS ?? 0)}s`, label: "LONGEST" },
      { value: `${Math.round(s.surf?.topWaveSpeedKmh ?? 0)}`, label: "WAVE KM/H" },
      avg,
      total,
    ];
  }

  if (record.sport === "coastal") {
    return [
      { value: s.distanceKm.toFixed(1), label: "KM" },
      { value: `${Math.round(s.topSpeedKmh)}`, label: "TOP KM/H" },
      avg,
      { value: formatReportDuration(s.movingTimeS), label: "MOVING" },
      total,
    ];
  }

  return [
    { value: s.distanceKm.toFixed(1), label: "KM" },
    { value: `${Math.round(s.verticalDescentM)}`, label: "M DOWN" },
    { value: `${Math.round(s.topSpeedKmh)}`, label: "TOP KM/H" },
    avg,
    total,
    { value: `${s.runCount}`, label: record.sport === "ski" ? "RUNS" : "DESCENTS" },
  ];
}

export function buildReportCardSummaryLine(record: SessionRecord): string {
  const s = record.stats;
  const totalSeconds = s.elapsedS > 0 ? s.elapsedS : Math.max(0, (record.endedAt - record.startedAt) / 1000);
  const parts = [
    `${s.distanceKm.toFixed(1)} km`,
    `${formatReportDuration(s.movingTimeS)} moving`,
    `${formatReportDuration(totalSeconds)} total`,
    `avg ${Math.round(s.avgMovingSpeedKmh)} km/h`,
    `top ${Math.round(s.topSpeedKmh)} km/h`,
  ];

  if (record.sport === "surf") {
    parts.push(`${s.surf?.waveCount ?? 0} waves`);
    parts.push(`longest ${Math.round(s.surf?.longestRideS ?? 0)}s`);
  } else if (record.sport !== "coastal") {
    parts.push(`${Math.round(s.verticalDescentM)} m descent`);
  }

  return parts.join(" | ");
}

export function buildReportCardConditionsStamp(
  input: ReportCardConditionsInput | null,
): ReportCardConditionsStamp | null {
  if (!input) return null;

  const score = normaliseScore(input.score);
  const scoreText =
    score == null
      ? null
      : `${scoreOutOf100Label(score)}${input.scoreLabel ? ` ${input.scoreLabel.trim()}` : ""}`;
  const place = input.placeName?.trim() || null;
  const primary = [place, scoreText].filter(Boolean).join(" | ");
  const secondary = [
    skillStampLabel(input.abilityLevel),
    input.tempC != null ? `${Math.round(input.tempC)}°C` : null,
    input.windKmh != null
      ? `${Math.round(input.windKmh)} km/h${input.windDir ? ` ${input.windDir}` : ""}`
      : null,
    input.sky?.trim() || null,
  ]
    .filter(Boolean)
    .join(" | ");

  if (!primary && !secondary) return null;
  return {
    primary: primary || "Conditions stamp",
    secondary: secondary || null,
  };
}

function normaliseScore(score: number | null | undefined): number | null {
  if (score == null || !Number.isFinite(score)) return null;
  const onTen = score > 10 ? score / 10 : score;
  const rounded = Math.round(onTen * 10) / 10;
  return Math.max(0, Math.min(10, rounded));
}
